import React, { useEffect, useState } from 'react';
import { Footer, Navbar } from './component';
import Cookies from 'universal-cookie';
import axios from 'axios';
import { LoginUrl, gmailLogin, verifyPasscodeUrl } from './serverurl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet } from 'react-helmet-async';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [processing, set_processing] = useState(false);
  const [disabled_status, set_disabled_status] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState('creator');
  const [passcode, setPasscode] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingPasscode, setPendingPasscode] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalFormErrors, setModalFormErrors] = useState({});
  const [modalType, setModalType] = useState('passcode'); // 'passcode' or 'direct'
  
  const cookies = new Cookies();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for passcode in URL on component mount
  useEffect(() => {
    const urlPasscode = searchParams.get('cbthost-passcode');
    if (urlPasscode) {
      setActiveTab('participant');
      setPasscode(urlPasscode);
      validatePasscodeFromUrl(urlPasscode);
    }
  }, [searchParams]);

  const validatePasscodeFromUrl = async (passcodeValue) => {
    try {
      set_processing(true);
      setIsAnimating(true);
      const response = await axios.post(verifyPasscodeUrl, { 
        passcode: passcodeValue.toUpperCase() 
      });
      
      if (response.data.valid) {
        // Check if user is already logged in
        const isAuthenticated = cookies.get('authenticated');
        const userType = cookies.get('user_type');
        
        if (isAuthenticated && userType === 'participant') {
          // Redirect directly to test
          navigate(`/participate/test/${passcodeValue}`);
        } else {
          // Show login modal for participants
          setPendingPasscode(passcodeValue);
          setModalType('passcode');
          setTimeout(() => setShowLoginModal(true), 500);
        }
      } else {
        toast.error('Invalid passcode. Please check with your instructor.');
      }
    } catch (error) {
      toast.error('Failed to validate passcode. Please try again.');
      console.error('Passcode validation error:', error);
    } finally {
      set_processing(false);
      setIsAnimating(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Email is invalid';
    if (!password) errors.password = 'Password is required';
    return errors;
  };

  const validateModalForm = () => {
    const errors = {};
    if (!modalEmail) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(modalEmail)) errors.email = 'Email is invalid';
    if (!modalPassword) errors.password = 'Password is required';
    return errors;
  };

  const validatePasscode = () => {
    const errors = {};
    if (!passcode) errors.passcode = 'Passcode is required';
    else if (passcode.length < 6) errors.passcode = 'Passcode must be at least 6 characters';
    return errors;
  };

  const handleCreatorLoginSuccess = (responseData) => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);

    cookies.set('auto_login_code', responseData.auto_login_code, { expires: expiryDate });
    cookies.set('auth_key', responseData.auth_key, { expires: expiryDate });
    cookies.set('surname', responseData.surname, { expires: expiryDate });
    cookies.set('name', responseData.name, { expires: expiryDate });
    cookies.set('email', responseData.email, { expires: expiryDate });
    cookies.set('authenticated', true, { expires: expiryDate });
    cookies.set('user_type', 'creator', { expires: expiryDate });

    toast.success(responseData.message);
    // console.log(response);
    navigate('/cbt_creator/dashboard');

  };

  const handleParticipantLoginSuccess = (responseData) => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);

    cookies.set('auto_login_code', responseData.auto_login_code, { expires: expiryDate });
    cookies.set('auth_key', responseData.auth_key, { expires: expiryDate });
    cookies.set('surname', responseData.surname, { expires: expiryDate });
    cookies.set('name', responseData.name, { expires: expiryDate });
    cookies.set('email', responseData.email, { expires: expiryDate });
    cookies.set('authenticated', true, { expires: expiryDate });
    cookies.set('user_type', 'participant', { expires: expiryDate });

    toast.success(responseData.message);
    
    // Redirect based on context
    if (pendingPasscode && modalType === 'passcode') {
      navigate(`/participate/verification?cbt_pass_code=${pendingPasscode}`);
      setShowLoginModal(false);
      setPendingPasscode('');
    } else {
      // Direct login to dashboard for viewing scores/data
      navigate('/participate/dashboard');
    }
  };

  const responseMessage = (response) => {
    try {
      const credentialData = jwtDecode(response.credential);
      set_processing(true);
      set_disabled_status(true);
      setIsAnimating(true);

      const data = {
        email: credentialData.email,
        name: credentialData.name,
        browser: true,
        user_type: activeTab // 'creator' or 'participant'
      };

      axios.post(gmailLogin, data)
        .then((response) => {
          if (response.data.serverstatus === 200) {
            if (activeTab === 'creator') {
              handleCreatorLoginSuccess(response.data);
            } else {
              handleParticipantLoginSuccess(response.data);
            }
          } else {
            toast.error(response.data.message || 'Authentication failed');
          }
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || 'Google login failed. Please try again.');
          console.error('Google login error:', error);
        })
        .finally(() => {
          set_processing(false);
          set_disabled_status(false);
          setIsAnimating(false);
        });
    } catch (error) {
      toast.error('Failed to process Google login. Please try again.');
      console.error('JWT decode error:', error);
      set_processing(false);
      set_disabled_status(false);
      setIsAnimating(false);
    }
  };

  const modalResponseMessage = (response) => {
    try {
      const credentialData = jwtDecode(response.credential);
      set_processing(true);
      set_disabled_status(true);

      const data = {
        email: credentialData.email,
        name: credentialData.name,
        browser: true,
        user_type: 'participant'
      };

      axios.post(gmailLogin, data)
        .then((response) => {
          if (response.data.serverstatus === 200) {
            handleParticipantLoginSuccess(response.data);
          } else {
            toast.error(response.data.message || 'Authentication failed');
          }
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || 'Google login failed. Please try again.');
          console.error('Google login error:', error);
        })
        .finally(() => {
          set_processing(false);
          set_disabled_status(false);
        });
    } catch (error) {
      toast.error('Failed to process Google login. Please try again.');
      console.error('JWT decode error:', error);
      set_processing(false);
      set_disabled_status(false);
    }
  };

  const errorMessage = (error) => {
    toast.error('Google login failed. Please try another method.');
    console.error('Google auth error:', error);
  };

  const login_form = (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    set_processing(true);
    set_disabled_status(true);
    setIsAnimating(true);

    const data = {
      email: email,
      password: password,
      browser: true,
      user_type: activeTab
    };

    axios.post(LoginUrl, data)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.serverstatus !== 401) {
            if (activeTab === 'creator') {
              handleCreatorLoginSuccess(response.data);
            } else {
              handleParticipantLoginSuccess(response.data);
            }
          } else {
            toast.error("Invalid password, please reset your password");
          }
        } else {
          toast.error('Please try again....');
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response) {
          switch (error.response.status) {
            case 400:
              toast.error('Invalid request data');
              break;
            case 401:
              toast.error('Invalid password, please reset your password');
              break;
            case 404:
              toast.error('Email does not exist');
              break;
            case 500:
              toast.error('Server error. Please try again later.');
              break;
            default:
              toast.error('Please try again...');
          }
        } else if (error.request) {
          toast.error('Network error. Please check your connection.');
        } else {
          toast.error('An unexpected error occurred.');
        }
        console.error('Login error:', error);
      })
      .finally(() => {
        set_processing(false);
        set_disabled_status(false);
        setIsAnimating(false);
      });
  };

  const modalLoginForm = (e) => {
    e.preventDefault();
    const errors = validateModalForm();
    setModalFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    set_processing(true);
    set_disabled_status(true);

    const data = {
      email: modalEmail,
      password: modalPassword,
      browser: true,
      user_type: 'participant'
    };

    axios.post(LoginUrl, data)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.serverstatus !== 401) {
            handleParticipantLoginSuccess(response.data);
          } else {
            toast.error("Invalid password, please reset your password");
          }
        } else {
          toast.error('Please try again....');
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response) {
          switch (error.response.status) {
            case 400:
              toast.error('Invalid request data');
              break;
            case 401:
              toast.error('Invalid password, please reset your password');
              break;
            case 404:
              toast.error('Email does not exist');
              break;
            case 500:
              toast.error('Server error. Please try again later.');
              break;
            default:
              toast.error('Please try again...');
          }
        } else if (error.request) {
          toast.error('Network error. Please check your connection.');
        } else {
          toast.error('An unexpected error occurred.');
        }
        console.error('Login error:', error);
      })
      .finally(() => {
        set_processing(false);
        set_disabled_status(false);
      });
  };

  const handleParticipate = async (e) => {
    e.preventDefault();
    const errors = validatePasscode();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    set_processing(true);
    set_disabled_status(true);
    setIsAnimating(true);

    try {
      // Validate passcode with backend
      const response = await axios.post(verifyPasscodeUrl, { 
        passcode: passcode.toUpperCase() 
      });
      
      if (response.data.valid) {
        // Check if user is logged in as participant
        const isAuthenticated = cookies.get('authenticated');
        const userType = cookies.get('user_type');
        
        if (isAuthenticated && userType === 'participant') {
          toast.success('Passcode validated! Redirecting to test...');
          navigate(`/participate/test/${passcode}`);
        } else {
          // Show login modal
          setPendingPasscode(passcode);
          setModalType('passcode');
          setTimeout(() => setShowLoginModal(true), 500);
          toast.info('Please login to continue with the test');
        }
      } else {
        toast.error('Invalid passcode. Please check with your instructor.');
      }
    } catch (error) {
      toast.error('Failed to validate passcode. Please try again.');
      console.error('Passcode validation error:', error);
    } finally {
      set_processing(false);
      set_disabled_status(false);
      setIsAnimating(false);
    }
  };

  const handleDirectParticipantLogin = () => {
    setModalType('direct');
    setShowLoginModal(true);
  };

  const handleTabChange = (tab) => {
    setIsAnimating(true);
    setActiveTab(tab);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const getModalTitle = () => {
    return modalType === 'passcode' ? 'Join Your Test' : 'Participant Login';
  };

  const getModalSubtitle = () => {
    return modalType === 'passcode' 
      ? 'Passcode validated! Login to continue' 
      : 'Login to access your test scores and dashboard';
  };

  const getLoginButtonText = () => {
    return modalType === 'passcode' ? 'Login & Start Test' : 'Login to Dashboard';
  };

  return (
    <>
      <Helmet>
        <meta id="description" name="description" content="cbthost - Login or participate in tests" />
        <title id="title">cbthost.com | Login & Participate</title>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-3xl">
          <div className="p-8">
            <div className="text-center mb-8">
              <a 
                href="https://cbthost.com" 
                className="inline-flex items-center text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors duration-300"
              >
                <i className="fas fa-graduation-cap mr-3 text-blue-500"></i>
                CBTHost
              </a>
              <h5 className="mt-6 text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Welcome to CBTHost
              </h5>
              <p className="mt-3 text-sm text-gray-600">
                Sign in to create tests or participate using a passcode
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex mb-8 bg-gray-100 rounded-xl p-1 shadow-inner">
              <button
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 transform ${
                  activeTab === 'creator' 
                    ? 'bg-blue-600 text-white shadow-lg scale-105' 
                    : 'text-gray-600 hover:text-blue-700 hover:bg-white hover:scale-102'
                }`}
                onClick={() => handleTabChange('creator')}
              >
                <i className={`fas ${activeTab === 'creator' ? 'fa-user-check' : 'fa-user-edit'} mr-2`}></i>
                Creator Login
              </button>
              <button
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 transform ${
                  activeTab === 'participant' 
                    ? 'bg-blue-600 text-white shadow-lg scale-105' 
                    : 'text-gray-600 hover:text-blue-700 hover:bg-white hover:scale-102'
                }`}
                onClick={() => handleTabChange('participant')}
              >
                <i className={`fas ${activeTab === 'participant' ? 'fa-key' : 'fa-key'} mr-2`}></i>
                Participate in Test
              </button>
            </div>

            {/* Creator Login Tab */}
            {activeTab === 'creator' && (
              <div className={`transition-all duration-500 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                <div className="flex justify-center mb-6">
                  <GoogleLogin
                    onSuccess={responseMessage}
                    onError={errorMessage}
                    theme="filled_blue"
                    size="large"
                    text="continue_with"
                    shape="pill"
                    width="280"
                  />
                </div>

                <div className="relative mt-8 mb-6">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-500 font-medium">Or continue with email</span>
                  </div>
                </div>

                {processing && (
                  <div className="mb-6 flex items-center justify-center text-blue-600 bg-blue-50 rounded-lg py-3 px-4 animate-pulse">
                    <i className="fas fa-spinner fa-spin mr-3 text-lg"></i>
                    <span className="font-medium">Please wait, authenticating user...</span>
                  </div>
                )}

                <form className="space-y-6" onSubmit={login_form}>
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                      <i className="fas fa-envelope mr-2 text-blue-500"></i>
                      Email address
                    </label>
                    <div className="relative group">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={disabled_status}
                        className={`w-full px-4 py-3 pl-12 border-2 rounded-xl transition-all duration-300 group-hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.email 
                            ? 'border-red-400 bg-red-50' 
                            : 'border-gray-300 bg-white'
                        } ${disabled_status ? 'opacity-60 cursor-not-allowed' : ''}`}
                        placeholder="you@example.com"
                      />
                      <i className="fas fa-envelope absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-blue-500 transition-colors duration-300"></i>
                    </div>
                    {formErrors.email && (
                      <p className="text-red-600 text-sm font-medium flex items-center mt-1 animate-shake">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                      <i className="fas fa-lock mr-2 text-blue-500"></i>
                      Password
                    </label>
                    <div className="relative group">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={disabled_status}
                        className={`w-full px-4 py-3 pl-12 border-2 rounded-xl transition-all duration-300 group-hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.password 
                            ? 'border-red-400 bg-red-50' 
                            : 'border-gray-300 bg-white'
                        } ${disabled_status ? 'opacity-60 cursor-not-allowed' : ''}`}
                        placeholder="••••••••"
                      />
                      <i className="fas fa-lock absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-blue-500 transition-colors duration-300"></i>
                    </div>
                    {formErrors.password && (
                      <p className="text-red-600 text-sm font-medium flex items-center mt-1 animate-shake">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {formErrors.password}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <a href="/password-reset" className="font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-300 hover:underline">
                        <i className="fas fa-question-circle mr-1"></i>
                        Forgot your password?
                      </a>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={disabled_status}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
                  >
                    {processing ? (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-3 text-lg"></i>
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-sign-in-alt mr-3 group-hover:scale-110 transition-transform duration-300"></i>
                        <span>Sign in as Creator</span>
                      </div>
                    )}
                  </button>
                </form>

                <div className="mt-8 text-center border-t border-gray-200 pt-6">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <a href="/sign-up" className="font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-300 hover:underline">
                      Create one now
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* Participant Tab */}
            {activeTab === 'participant' && (
              <div className={`transition-all duration-500 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8 shadow-sm">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <i className="fas fa-info-circle text-blue-500 text-lg"></i>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-sm font-semibold text-blue-800">How to participate</h4>
                      <p className="text-sm text-blue-600 mt-2 leading-relaxed">
                        Enter the passcode provided by your instructor to join and take your test.
                        You'll need to login as a participant to take tests.
                      </p>
                    </div>
                  </div>
                </div>

                {processing && (
                  <div className="mb-6 flex items-center justify-center text-blue-600 bg-blue-50 rounded-xl py-3 px-4 animate-pulse">
                    <i className="fas fa-spinner fa-spin mr-3 text-lg"></i>
                    <span className="font-medium">Validating passcode, please wait...</span>
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleParticipate}>
                  <div className="space-y-2">
                    <label htmlFor="passcode" className="block text-sm font-semibold text-gray-700">
                      <i className="fas fa-key mr-2 text-blue-500"></i>
                      Test Passcode
                    </label>
                    <div className="relative group">
                      <input
                        id="passcode"
                        name="passcode"
                        type="text"
                        value={passcode}
                        onChange={(e) => setPasscode(e.target.value.toUpperCase())}
                        disabled={disabled_status}
                        className={`w-full px-4 py-3 pl-12 border-2 rounded-xl transition-all duration-300 group-hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase font-mono ${
                          formErrors.passcode 
                            ? 'border-red-400 bg-red-50' 
                            : 'border-gray-300 bg-white'
                        } ${disabled_status ? 'opacity-60 cursor-not-allowed' : ''}`}
                        placeholder="cbthost-pssi-xxxx"
                        // maxLength="20"
                      />
                      <i className="fas fa-key absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-blue-500 transition-colors duration-300"></i>
                    </div>
                    {formErrors.passcode && (
                      <p className="text-red-600 text-sm font-medium flex items-center mt-1 animate-shake">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {formErrors.passcode}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      <i className="fas fa-lightbulb mr-2 text-yellow-500"></i>
                      The passcode is case-insensitive and provided by your instructor
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={disabled_status}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
                  >
                    {processing ? (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-3 text-lg"></i>
                        <span>Validating...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-play-circle mr-3 group-hover:scale-110 transition-transform duration-300"></i>
                        <span>Validate & Join Test</span>
                      </div>
                    )}
                  </button>
                </form>

                {/* Direct Participant Login Option */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-4">
                      Already a participant? Access your test scores and dashboard
                    </p>
                    <button
                      onClick={handleDirectParticipantLogin}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl group"
                    >
                      <div className="flex items-center justify-center">
                        <i className="fas fa-chart-line mr-3 group-hover:scale-110 transition-transform duration-300"></i>
                        <span>View My Scores & Dashboard</span>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Need help?{' '}
                    <a href="https://cbthost.com/support" className="font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-300 hover:underline">
                      Contact support
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Login Modal with Full Login Form */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity duration-300 ease-in-out" 
              aria-hidden="true"
              onClick={() => setShowLoginModal(false)}
            >
              <div className="absolute inset-0 bg-gray-600 opacity-75 backdrop-blur-sm"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full animate-scale-in">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <i className={`fas ${modalType === 'passcode' ? 'fa-key' : 'fa-chart-line'} text-white text-xl`}></i>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-bold text-white">
                        {getModalTitle()}
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {getModalSubtitle()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLoginModal(false)}
                    className="text-white hover:text-blue-200 transition-colors duration-200"
                  >
                    <i className="fas fa-times text-lg"></i>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="bg-white px-6 pt-6 pb-4">
                {/* Passcode Info - Only show for passcode modal */}
                {modalType === 'passcode' && (
                  <div className="mb-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800">Test Passcode</p>
                        <p className="text-2xl font-bold text-blue-900 font-mono">{pendingPasscode}</p>
                      </div>
                      <div className="text-green-500">
                        <i className="fas fa-check-circle text-2xl"></i>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      <i className="fas fa-info-circle mr-1"></i>
                      Passcode validated successfully. Please login to start your test.
                    </p>
                  </div>
                )}

                {/* Direct Login Info - Only show for direct modal */}
                {modalType === 'direct' && (
                  <div className="mb-6 bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <i className="fas fa-chart-line text-green-500 text-xl"></i>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800">Access Your Dashboard</p>
                        <p className="text-xs text-green-600 mt-1">
                          Login to view your test scores, previous results, and continue ongoing tests.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Google Login */}
                <div className="mb-6">
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={modalResponseMessage}
                      onError={errorMessage}
                      theme="filled_blue"
                      size="large"
                      text="continue_with"
                      shape="pill"
                      width="100%"
                    />
                  </div>
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-500 font-medium">Or login with email</span>
                  </div>
                </div>

                {/* Login Form */}
                <form onSubmit={modalLoginForm} className="space-y-4">
                  <div>
                    <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700 mb-1">
                      <i className="fas fa-envelope mr-2 text-blue-500"></i>
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        id="modal-email"
                        type="email"
                        value={modalEmail}
                        onChange={(e) => setModalEmail(e.target.value)}
                        disabled={disabled_status}
                        className={`w-full px-4 py-3 pl-11 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          modalFormErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        } ${disabled_status ? 'opacity-60 cursor-not-allowed' : ''}`}
                        placeholder="you@example.com"
                      />
                      <i className="fas fa-envelope absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                    {modalFormErrors.email && (
                      <p className="text-red-600 text-sm mt-1 flex items-center">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {modalFormErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="modal-password" className="block text-sm font-medium text-gray-700 mb-1">
                      <i className="fas fa-lock mr-2 text-blue-500"></i>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="modal-password"
                        type="password"
                        value={modalPassword}
                        onChange={(e) => setModalPassword(e.target.value)}
                        disabled={disabled_status}
                        className={`w-full px-4 py-3 pl-11 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          modalFormErrors.password ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        } ${disabled_status ? 'opacity-60 cursor-not-allowed' : ''}`}
                        placeholder="••••••••"
                      />
                      <i className="fas fa-lock absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                    {modalFormErrors.password && (
                      <p className="text-red-600 text-sm mt-1 flex items-center">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {modalFormErrors.password}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <a href="/password-reset" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                      <i className="fas fa-question-circle mr-1"></i>
                      Forgot your password?
                    </a>
                    <a href="/sign-up" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                      <i className="fas fa-user-plus mr-1"></i>
                      Create account
                    </a>
                  </div>

                  <button
                    type="submit"
                    disabled={disabled_status}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
                  >
                    {processing ? (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-3"></i>
                        <span>Logging in...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-sign-in-alt mr-3 group-hover:scale-110 transition-transform duration-300"></i>
                        <span>{getLoginButtonText()}</span>
                      </div>
                    )}
                  </button>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
                <div className="text-center">
                  <p className="text-xs text-gray-500">
                    By continuing, you agree to our{' '}
                    <a href="/terms" className="text-blue-600 hover:text-blue-500 font-medium">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-blue-600 hover:text-blue-500 font-medium">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        toastClassName="rounded-xl"
        progressClassName="bg-gradient-to-r from-blue-500 to-indigo-500"
      />

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .hover\\:shadow-3xl:hover {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </>
  );
}

export default Login;